import { APP_BASE_HREF } from '@angular/common';
import {
  provideHttpClient,
  withFetch,
  withInterceptors,
} from '@angular/common/http';
import {
  EnvironmentProviders,
  importProvidersFrom,
  inject,
  provideAppInitializer,
  Provider,
  provideZonelessChangeDetection,
} from '@angular/core';
import {
  provideRouter,
  withInMemoryScrolling,
  withRouterConfig,
} from '@angular/router';
import { AUTH_CONFIG, provideAuth } from '@auth';
import {
  COMMON_CONFIG,
  CookieProService,
  requestActionInterceptor,
  requestSourceInterceptor,
  whiteLabelClientInterceptor,
} from '@common';
import {
  authConfigFactory,
  commonConfigFactory,
  Config,
  CONFIG,
  Environment,
  ENVIRONMENT,
  provideConfig,
  VariantConfig,
} from '@config';
import { DOMAIN_ID, provideContentDefaults } from '@content';
import { provideFormsAddressDefaults } from '@forms';
import { GA_CONFIG, gaConfigFactory, provideGA } from '@ga';
import { provideI18nDefaults } from '@i18n';
import { provideSEO, SEO_CONFIG, seoConfigFactory } from '@seo';
import { posthogHttpErrorInterceptor, TrackingService } from '@tracking';
import {
  RECAPTCHA_V3_SITE_KEY,
  RecaptchaModule,
  RecaptchaV3Module,
} from 'ng-recaptcha-2';

export function provideApp(
  variantConfig: VariantConfig,
  environment: Environment,
): Provider | EnvironmentProviders[] {
  return [
    provideAuth(),
    provideSEO(),
    provideAppInitializer(() => inject(CookieProService).initializeCookiePro()),
    provideAppInitializer(() => inject(TrackingService).initialise()),
    provideZonelessChangeDetection(),
    provideFormsAddressDefaults(),
    provideContentDefaults({ olrVersion: variantConfig.olrVersion }),
    provideI18nDefaults(
      variantConfig.countryIsoNumber
        ? { countryIsoNumber: variantConfig.countryIsoNumber }
        : {},
    ),
    provideRouter(
      variantConfig.routes,
      withRouterConfig({
        paramsInheritanceStrategy: 'always',
      }),
      withInMemoryScrolling({
        scrollPositionRestoration: 'top',
      }),
    ),
    provideHttpClient(
      withInterceptors([
        requestSourceInterceptor,
        requestActionInterceptor,
        whiteLabelClientInterceptor,
        posthogHttpErrorInterceptor,
      ]),
      withFetch(),
    ),
    importProvidersFrom(RecaptchaModule, RecaptchaV3Module),
    provideConfig(),
    provideGA(),
    {
      provide: APP_BASE_HREF,
      useValue: variantConfig.baseHref,
    },
    {
      provide: AUTH_CONFIG,
      useFactory: authConfigFactory,
      deps: [CONFIG],
    },
    {
      provide: COMMON_CONFIG,
      useFactory: commonConfigFactory,
      deps: [CONFIG],
    },
    {
      provide: DOMAIN_ID,
      useFactory: (config: Config) => config.domainId,
      deps: [CONFIG],
    },
    {
      provide: ENVIRONMENT,
      useValue: environment,
    },
    {
      provide: GA_CONFIG,
      useFactory: gaConfigFactory,
      deps: [CONFIG],
    },
    {
      provide: RECAPTCHA_V3_SITE_KEY,
      useFactory: (config: Config) => config.recaptchaSiteKey,
      deps: [CONFIG],
    },
    {
      provide: SEO_CONFIG,
      useFactory: seoConfigFactory,
      deps: [CONFIG],
    },
  ];
}
